import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getCanadaTorontoNow } from '@/utils/dateTime';

// Types for the search trip data
export interface PassengerData {
  adult: number;
  senior: number;
  child: number;
  special_assistance: boolean;
  special_assistance_reason?: string;
}

export interface VehicleData {
  height: string;
  length: string;
  vehicle_return: boolean;
  type?: string;
}

export interface SearchTripState {
  // Form data
  searchData: SearchTripData;

  // UI display values
  vehicleCount: number; // 0 for walk-on, 1+ for vehicles

  // Available routes
  routes: RouteData[];
  locations: string[];

  // UI state
  tripType: 'roundtrip' | 'oneway';
  dateError: string;

  // Currently selected dates in SelectCrossing (UI state)
  currentSelectedDates: {
    departure: string; // YYYY-MM-DD format
    returning?: string; // YYYY-MM-DD format
  };

  // API state
  isLoading: boolean;
  searchResults: any[];
  error: string | null;
}

export interface RouteData {
  id: string;
  departure: string;
  destination: string;
  status: boolean;
  routeGroupId: string;
}

export interface SearchTripData {
  routeId: string;
  passenger: PassengerData;
  vehicle: VehicleData;
  departure: string; // YYYY-MM-DD format
  returning?: string; // YYYY-MM-DD format
  round: 'one' | 'two';
}



// Helper function to format date from DD/MM/YYYY to YYYY-MM-DD
const formatDateForAPI = (dateString: string): string => {
  const [day, month, year] = dateString.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
const formatDateForDisplay = (dateString: string): string => {
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Get current date and tomorrow's date in DD/MM/YYYY format - Fixed: Using Canadian time
const getCurrentDates = () => {
  const canadianTime = getCanadaTorontoNow();
  
  // Format today
  const todayDay = canadianTime.day.toString().padStart(2, '0');
  const todayMonth = (canadianTime.monthIndex + 1).toString().padStart(2, '0');
  const todayYear = canadianTime.year;
  const todayString = `${todayDay}/${todayMonth}/${todayYear}`;
  
  // Calculate tomorrow using string manipulation
  let tomorrowDay = canadianTime.day + 1;
  let tomorrowMonth = canadianTime.monthIndex + 1;
  let tomorrowYear = canadianTime.year;
  
  // Handle month rollover
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  // Check for leap year
  if (tomorrowYear % 4 === 0 && (tomorrowYear % 100 !== 0 || tomorrowYear % 400 === 0)) {
    daysInMonth[1] = 29;
  }
  
  if (tomorrowDay > daysInMonth[canadianTime.monthIndex]) {
    tomorrowDay = 1;
    tomorrowMonth++;
    if (tomorrowMonth > 12) {
      tomorrowMonth = 1;
      tomorrowYear++;
    }
  }
  
  const tomorrowString = `${tomorrowDay.toString().padStart(2, '0')}/${tomorrowMonth.toString().padStart(2, '0')}/${tomorrowYear}`;

  return {
    departing: todayString,
    returning: tomorrowString
  };
};

const currentDates = getCurrentDates();

const initialState: SearchTripState = {
  searchData: {
    routeId: '',
    passenger: {
      adult: 0, // Start with 0 passengers
      senior: 0,
      child: 0,
      special_assistance: false,
      special_assistance_reason: ''
    },
    vehicle: {
      height: 'standard',
      length: 'standard',
      vehicle_return: true,
      type: 'Foot Passenger' // Start with Foot Passenger (no vehicle)
    },
    departure: formatDateForAPI(currentDates.departing),
    returning: formatDateForAPI(currentDates.returning),
    round: 'two'
  },
  vehicleCount: 0, // Start with 0 vehicles
  routes: [],
  locations: [],
  tripType: 'roundtrip',
  dateError: '',
  currentSelectedDates: {
    departure: formatDateForAPI(currentDates.departing),
    returning: formatDateForAPI(currentDates.returning)
  },
  isLoading: false,
  searchResults: [],
  error: null
};

// Async thunk for fetching routes
export const fetchRoutes = createAsyncThunk(
  'searchTrip/fetchRoutes',
  async (_, { rejectWithValue }) => {
    try {
      const { getRoutes } = await import('@/lib/api/trip');
      const response = await getRoutes();

      if (response.success && response.data?.route) {
        return response.data.route;
      } else {
        throw new Error('Failed to fetch routes');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch routes');
    }
  }
);

// Async thunk for searching trips with custom dates
export const searchTripsWithDates = createAsyncThunk(
  'searchTrip/searchTripsWithDates',
  async (customDates: { departure?: string; returning?: string }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { searchTrip: SearchTripState };
      const { searchData, vehicleCount } = state.searchTrip;

      // Validate that we have at least 1 passenger
      const totalPassengers = searchData.passenger.adult + searchData.passenger.senior + searchData.passenger.child;
      if (totalPassengers === 0) {
        return rejectWithValue('At least one passenger is required');
      }

      // Build the API URL with query parameters
      const params = new URLSearchParams();
      params.append('route', searchData.routeId);
      params.append('passenger', JSON.stringify(searchData.passenger));

      // Handle vehicle parameter - empty string if no vehicle selected
      if (vehicleCount === 0) {
        params.append('vehicle', '""');
      } else {
        params.append('vehicle', JSON.stringify(searchData.vehicle));
      }

      // Use custom dates if provided, otherwise use stored dates
      params.append('departure', customDates.departure || searchData.departure);
      if (searchData.round === 'two') {
        const returningDate = customDates.returning || searchData.returning;
        if (returningDate) {
          params.append('returning', returningDate);
        }
      }
      params.append('round', searchData.round);

      const { default: api } = await import('@/lib/api');
      const response = await api.get(`/trip/search?${params.toString()}`);

      // If successful, dispatch the results to tripResults slice
      if (response.data.success && response.data.data) {
        const { setTripResults } = await import('./tripResultsSlice');
        dispatch(setTripResults(response.data.data));
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search trips');
    }
  }
);

// Async thunk for searching trips
export const searchTrips = createAsyncThunk(
  'searchTrip/searchTrips',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { searchTrip: SearchTripState };
      const { searchData, vehicleCount } = state.searchTrip;


      // Allow 0 passengers (they can travel with vehicle only)
      const totalPassengers = searchData.passenger.adult + searchData.passenger.senior + searchData.passenger.child;
      
      // Validate that we have either passengers OR vehicle
      if (totalPassengers === 0 && vehicleCount === 0) {
        return rejectWithValue('At least one passenger or vehicle is required');
      }

      // Build the API URL with query parameters
      const params = new URLSearchParams();
      params.append('route', searchData.routeId);
      params.append('passenger', JSON.stringify(searchData.passenger));

      // Handle vehicle parameter - empty string if no vehicle selected
      if (vehicleCount === 0) {
        params.append('vehicle', '""');
      } else {
        params.append('vehicle', JSON.stringify(searchData.vehicle));
      }

      params.append('departure', searchData.departure);
      if (searchData.returning && searchData.round === 'two') {
        params.append('returning', searchData.returning);
      }
      params.append('round', searchData.round);


      const { default: api } = await import('@/lib/api');
      const response = await api.get(`/trip/search?${params.toString()}`);


      // If successful, dispatch the results to tripResults slice
      if (response.data.success && response.data.data) {
        const { setTripResults } = await import('./tripResultsSlice');
        dispatch(setTripResults(response.data.data));
      } else {

      }

      return response.data;
    } catch (error: unknown) {
      return rejectWithValue((error as any)?.response?.data?.message || 'Failed to search trips');
    }
  }
);

const searchTripSlice = createSlice({
  name: 'searchTrip',
  initialState,
  reducers: {
    // Trip type actions
    setTripType: (state, action: PayloadAction<'roundtrip' | 'oneway'>) => {
      state.tripType = action.payload;
      state.searchData.round = action.payload === 'roundtrip' ? 'two' : 'one';

      // Clear returning date for one-way trips
      if (action.payload === 'oneway') {
        state.searchData.returning = undefined;
      } else {
        // Set returning date to departure date if not set
        if (!state.searchData.returning) {
          state.searchData.returning = state.searchData.departure;
        }
      }
    },

    // Route actions
    setFromLocation: (state, action: PayloadAction<string>) => {
      const selectedRoute = state.routes.find(route =>
        route.departure === action.payload
      );

      if (selectedRoute) {
        state.searchData.routeId = selectedRoute.id;

        // Find the corresponding return route
        const returnRoute = state.routes.find(route =>
          route.departure === selectedRoute.destination &&
          route.destination === selectedRoute.departure
        );

        if (returnRoute) {
          // Auto-select the return destination
          // This is handled in the component
        }
      }
    },

    setToLocation: (state, action: PayloadAction<string>) => {
      const selectedRoute = state.routes.find(route =>
        route.destination === action.payload
      );

      if (selectedRoute) {
        state.searchData.routeId = selectedRoute.id;
      }
    },

    // Date actions
    setDepartureDate: (state, action: PayloadAction<string>) => {
      const formattedDate = formatDateForAPI(action.payload);
      state.searchData.departure = formattedDate;

      // Clear date error
      state.dateError = '';

      // If returning date exists and is before the new departure date, update it to match departure date
      if (state.searchData.returning) {
        // Compare YYYY-MM-DD formatted dates directly
        if (state.searchData.returning < formattedDate) {
          state.searchData.returning = formattedDate;
        }
      }
    },

    setReturningDate: (state, action: PayloadAction<string>) => {
      const formattedDate = formatDateForAPI(action.payload);
      state.searchData.returning = formattedDate;
      // Clear any existing date error since we prevent invalid selection at UI level
      state.dateError = '';
    },

    // Passenger actions
    setPassengerData: (state, action: PayloadAction<Partial<PassengerData>>) => {
      const updatedPassenger = {
        ...state.searchData.passenger,
        ...action.payload
      };

      // Allow 0 passengers (they can select vehicle instead)
      state.searchData.passenger = updatedPassenger;
    },

    // Vehicle actions
    setVehicleData: (state, action: PayloadAction<Partial<VehicleData>>) => {
      state.searchData.vehicle = {
        ...state.searchData.vehicle,
        ...action.payload
      };
    },

    // Vehicle count actions
    setVehicleCount: (state, action: PayloadAction<number>) => {
      state.vehicleCount = Math.max(0, action.payload); // Ensure non-negative

      // Update vehicle type based on count
      if (state.vehicleCount === 0) {
        state.searchData.vehicle.type = 'Foot Passenger';
      } else if (!state.searchData.vehicle.type || state.searchData.vehicle.type === 'Foot Passenger') {
        state.searchData.vehicle.type = 'Car'; // Default to car when adding vehicle
      }
    },

    // Error actions
    setDateError: (state, action: PayloadAction<string>) => {
      state.dateError = action.payload;
    },

    clearDateError: (state) => {
      state.dateError = '';
    },

    // Reset search results
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.error = null;
    },

    // Actions for currently selected dates (UI state)
    setCurrentSelectedDepartureDate: (state, action: PayloadAction<string>) => {
      state.currentSelectedDates.departure = action.payload;
    },

    setCurrentSelectedReturnDate: (state, action: PayloadAction<string>) => {
      state.currentSelectedDates.returning = action.payload;
    },

    setCurrentSelectedDates: (state, action: PayloadAction<{ departure: string; returning?: string }>) => {
      state.currentSelectedDates.departure = action.payload.departure;
      if (action.payload.returning) {
        state.currentSelectedDates.returning = action.payload.returning;
      }
    },

    // Reset to initial state (for homepage)
    resetToInitialState: (state) => {
      const currentDates = getCurrentDates();

      state.searchData.passenger = {
        adult: 0,
        senior: 0,
        child: 0,
        special_assistance: false,
        special_assistance_reason: ''
      };

      state.vehicleCount = 0;
      state.searchData.vehicle = {
        height: 'standard',
        length: 'standard',
        vehicle_return: true,
        type: 'Foot Passenger'
      };

      state.tripType = 'roundtrip';
      state.searchData.round = 'two';
      state.searchData.departure = formatDateForAPI(currentDates.departing);
      state.searchData.returning = formatDateForAPI(currentDates.returning);
      state.currentSelectedDates.departure = formatDateForAPI(currentDates.departing);
      state.currentSelectedDates.returning = formatDateForAPI(currentDates.returning);
      state.dateError = '';
    }
  },
  extraReducers: (builder) => {
    // Fetch routes
    builder
      .addCase(fetchRoutes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoutes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.routes = action.payload;

        // Extract unique locations
        const uniqueLocations = new Set<string>();
        action.payload.forEach((route: RouteData) => {
          uniqueLocations.add(route.departure);
          uniqueLocations.add(route.destination);
        });

        state.locations = Array.from(uniqueLocations);

        // Set default route if available
        if (action.payload.length > 0) {
          const firstRoute = action.payload[0];
          state.searchData.routeId = firstRoute.id;
        }
      })
      .addCase(fetchRoutes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;

        // Fallback locations
        state.locations = ['AER', 'TUK'];
      });

    // Search trips
    builder
      .addCase(searchTrips.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchTrips.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchTrips.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Handle date-change custom search loading without referencing the thunk const
      .addMatcher(
        (action) => action.type === 'searchTrip/searchTripsWithCustomDates/pending',
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type === 'searchTrip/searchTripsWithCustomDates/fulfilled',
        (state) => {
          state.isLoading = false;
        }
      )
      .addMatcher(
        (action) => action.type === 'searchTrip/searchTripsWithCustomDates/rejected',
        (state) => {
          state.isLoading = false;
          state.error = 'Failed to search trips';
        }
      );
  }
});

// Async thunk for searching trips with custom dates (for SelectCrossing date changes)
export const searchTripsWithCustomDates = createAsyncThunk(
  'searchTrip/searchTripsWithCustomDates',
  async (customDates: { departure?: string; returning?: string }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { searchTrip: SearchTripState };
      const { searchData, vehicleCount } = state.searchTrip;

      // Allow 0 passengers (they can travel with vehicle only)
      const totalPassengers = searchData.passenger.adult + searchData.passenger.senior + searchData.passenger.child;
      
      // Validate that we have either passengers OR vehicle
      if (totalPassengers === 0 && vehicleCount === 0) {
        return rejectWithValue('At least one passenger or vehicle is required');
      }

      // Build the API URL with query parameters
      const params = new URLSearchParams();
      params.append('route', searchData.routeId);
      params.append('passenger', JSON.stringify(searchData.passenger));

      // Handle vehicle parameter - empty string if no vehicle selected
      if (vehicleCount === 0) {
        params.append('vehicle', '""');
      } else {
        params.append('vehicle', JSON.stringify(searchData.vehicle));
      }

      // Use custom dates if provided, otherwise use stored dates
      params.append('departure', customDates.departure || searchData.departure);
      if (searchData.round === 'two') {
        const returningDate = customDates.returning || searchData.returning;
        if (returningDate) {
          params.append('returning', returningDate);
        }
      }
      params.append('round', searchData.round);

      const { default: api } = await import('@/lib/api');
      const response = await api.get(`/trip/search?${params.toString()}`);

      // If successful, dispatch the results to tripResults slice
      if (response.data.success && response.data.data) {
        const { setTripResults } = await import('./tripResultsSlice');
        dispatch(setTripResults(response.data.data));
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search trips');
    }
  }
);

export const {
  setTripType,
  setFromLocation,
  setToLocation,
  setDepartureDate,
  setReturningDate,
  setPassengerData,
  setVehicleData,
  setVehicleCount,
  setDateError,
  clearDateError,
  clearSearchResults,
  setCurrentSelectedDepartureDate,
  setCurrentSelectedReturnDate,
  setCurrentSelectedDates,
  resetToInitialState
} = searchTripSlice.actions;

export default searchTripSlice.reducer;